import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import List from "@material-ui/core/List";
import Grid from "@material-ui/core/Grid";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SettingsIcon from "@material-ui/icons/Settings";
import Drawer from "@material-ui/core/Drawer";
import { makeStyles } from "@material-ui/core/styles";
import MenuIcon from "@material-ui/icons/Menu";
import { Collapse, ListItemSecondaryAction } from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import { ArrowBack } from "@material-ui/icons";
import Vibrant from "node-vibrant/lib/bundle";
import clsx from "clsx";
import ListItem from "../../components/ListItem";
import useAuth from "../../hooks/useAuth";
import { ROUTES } from "../../menuRoutes";
import AlertDialog from "../Dialog";
import { parseJwt } from "../../utils";
import menuLogo from "../../assets/img/weedar.svg";
import menuSmallLogo from "../../assets/img/weedar-logo.svg";
import {
  APP_VERSION,
  ROLES,
  SETTINGS,
  fontFamilies,
  HOME,
  ordersTypeSidebar,
  BRANDS,
} from "../../constants";
import { mobileSize, useCheckIsMobile } from "../../utils/responsive";
import { UserContext } from "../../context/User/context";
import CustomLink from "../CustomLink";
import { BrandContext } from "../../context/Brand/context";
import {
  routesBrandAdmin,
  routesBudtender,
  routesDriver,
} from "../../menuRoutesBrand";
import { WebSocketContext } from "../../context/WebSocket/context";
import { MenuContext } from "../../context/Menu/context";
import chevronDownSvg from "../../assets/img/chevron-down-white.svg";
import LightTooltip from "../LightTooltip";
import sidbarActiveBg from "../../assets/img/sidebarActiveBg.jpg";
import sidebarActiveMinBg from "../../assets/img/sidebarActiveMinBg.jpg";
import MenuBrandsPopper from "../../pages/MenuBrandsPopper";
import MenuBrandActivation from "../MenuBrandActivation";

const useMenuLogoStyles = makeStyles((theme) => ({
  menuLogoWrapper: {
    position: "relative",
    marginBottom: 40,
    width: "100%",
    maxWidth: "100%",
    textAlign: "center",
    height: ({ isMobile }) => (isMobile ? 42 : "auto"),

    "&::after": {
      content: '""',
      position: "absolute",
      left: 8,
      right: 8,
      bottom: -20,
      height: 1,
      background: "rgba(255, 255, 255, 0.2)",
    },

    [theme.breakpoints.down(mobileSize)]: {
      marginBottom: 0,

      "&::after": {
        display: "none",
      },
    },
  },
  menuLogo: {
    height: "70px",
    objectFit: "contain",
    filter: ({ isLightIcon }) => (isLightIcon ? "" : "invert(1)"),
    [theme.breakpoints.down(mobileSize)]: {
      // width: ({ isMobile }) => (!isMobile ? 160 : null),
      height: 42,
      textAlign: "center",
    },
    width: "100%",
  },
  menuLogoGag: {
    objectFit: "contain",
    height: "75px",
    width: "100%",
  },
}));

const MenuLogo = ({ isHide, isLightIcon, isMobile }) => {
  const { isAnyAdmin, isBrandManager, isBrandBudtender, isBrandDriver } =
    useAuth();
  const { brand, isBrandRoute } = useContext(BrandContext);
  const classes = useMenuLogoStyles({
    isLightIcon,
    isBrandRoute:
      isBrandRoute || isBrandManager || isBrandBudtender || isBrandDriver,
    isMobile,
  });
  let src = "";
  if (isBrandManager || isBrandBudtender || isBrandDriver) {
    src = brand?.logo?.url;
  } else if (isAnyAdmin) {
    if (isBrandRoute) {
      src = brand?.logo?.url;
    } else {
      src = isHide ? menuSmallLogo : menuLogo;
    }
  }

  return (
    <div className={classes.menuLogoWrapper}>
      {src ? (
        <img className={classes.menuLogo} src={src} alt="weedar" />
      ) : (
        <div className={classes.menuLogoGag}></div>
      )}
    </div>
  );
};

export const Menu = ({ user }) => {
  const {
    logOut,
    isBrandBudtender,
    isBrandDriver,
    isBrandManager,
    isAnyAdmin,
  } = useAuth();
  const isMobile = useCheckIsMobile();
  const { partner } = useContext(UserContext);
  const { dataObject: webSocketDataObject, budtenderOrdersCount } =
    useContext(WebSocketContext);
  const {
    isBrandRoute,
    brandId,
    brand,
    isBrandUser,
    brands,
    brandIsActivated,
  } = useContext(BrandContext);
  const { isHideMenu: isHide, setIsHideMenu: setIsHide } =
    useContext(MenuContext);
  const [isOpenLogout, setIsOpenLogout] = React.useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const { pathname } = useLocation();
  const userInfo = parseJwt(user);

  const routes = useMemo(() => {
    if (isBrandManager) {
      return brandId ? routesBrandAdmin(brandId) : [];
    }
    if (isBrandBudtender) {
      return brandId ? routesBudtender : [];
    }
    if (isBrandDriver) {
      return brandId ? routesDriver : [];
    }
    if (isBrandRoute) {
      return brandId ? routesBrandAdmin(brandId) : [];
    }
    return ROUTES;
  }, [brandId, isBrandRoute, isBrandManager, isBrandBudtender, isBrandDriver]);

  const [openMenuItems, setOpenMenuItems] = useState({});

  useEffect(() => {
    if (!routes.length) return;
    setOpenMenuItems(
      routes.reduce((acc, item) => {
        if (!item.children?.length) return acc;
        return {
          ...acc,
          [item.name]: !!item.children?.find((c) => c.path === pathname),
        };
      }, {})
    );
  }, [routes]);

  const ordersCountBrand = useMemo(() => {
    if (userInfo.role.code === ROLES.brandBudtender)
      return budtenderOrdersCount;

    if (
      !webSocketDataObject["brand_ordersCount"] ||
      !webSocketDataObject["brand_ordersCount"][ordersTypeSidebar.NEW]
    )
      return 0;

    return (
      webSocketDataObject["brand_ordersCount"][ordersTypeSidebar.NEW][0] +
      webSocketDataObject["brand_ordersCount"][ordersTypeSidebar.NEW][1]
    );
  }, [webSocketDataObject, userInfo, budtenderOrdersCount]);

  const ordersCountMain = useMemo(() => {
    if (isBrandRoute) return ordersCountBrand;
    if (!webSocketDataObject["collectibles_ordersCount"]) return 0;
    return (
      webSocketDataObject["collectibles_ordersCount"][0] +
      webSocketDataObject["collectibles_ordersCount"][1]
    );
  }, [webSocketDataObject, isBrandRoute, ordersCountBrand]);

  const supportCount = useMemo(() => {
    if (!webSocketDataObject["supportCounter"]) return 0;
    return webSocketDataObject["supportCounter"].count;
  }, [webSocketDataObject]);

  const messageCount = useMemo(() => {
    if (!webSocketDataObject["messagesCount"]) return 0;
    return webSocketDataObject["messagesCount"].count;
  }, [webSocketDataObject]);

  const handleClickOpen = () => setIsOpenLogout(true);

  const searchPathname = (regexp, pathname) => {
    if (regexp === HOME) return regexp === pathname;
    const re = new RegExp(regexp.replace(":id", "\\d+"), "gi");
    return pathname.match(re)?.length > 0;
  };

  const useStyles = makeStyles((theme) => ({
    drawer: {
      flexShrink: 0,
      width: "100%",
      height: "100vh",
      [theme.breakpoints.down(mobileSize)]: {
        height: 76,
        width: "100%",
      },
    },
    drawerPaper: {
      height: () => (isMobile ? "auto" : null),
      position: "relative",
      marginLeft: 24,
      background: "#000",
      borderRadius: 28,
      padding: isHide ? "24px 0 16px" : "16px 0 16px",
      maxHeight: "calc(100% - 80px)",
      marginTop: 24,
      display: "block",
      borderRight: 0,
      justifyContent: "space-between",
      "&::-webkit-scrollbar": {
        display: "none",
      },
      "-ms-overflow-style": "none",
      "scrollbar-width": "none",
      [theme.breakpoints.down(mobileSize)]: {
        position: "fixed",
        width: "calc(100% - 16px)",
        transition: "max-height 0.2s ease-in",
        maxHeight: () => (menuIsOpen ? null : 64),
        minHeight: 64,
        borderRadius: 20,
        marginTop: 12,
        marginLeft: 8,
        marginRight: 8,
        padding: 0,
      },
    },
    link: {
      textDecoration: "none",
      color: "#fff",
      [theme.breakpoints.down(mobileSize)]: {},
    },
    linkItem: {
      height: 48,
      margin: "2px 0",
      padding: isHide ? 13 : 16,
      [theme.breakpoints.down(mobileSize)]: {
        margin: "8px",
        width: "auto",
      },
    },
    linkItemActiveChild: {
      background: `url(${
        isHide ? sidebarActiveMinBg : sidbarActiveBg
      }) lightgray 50% / cover no-repeat`,
      color: "black",
      "& .MuiListItemIcon-root": {
        filter: "invert(1)",
      },
      "&:hover": {
        color: "black",
      },
    },
    linkText: {
      fontFamily: fontFamilies[500],
      fontSize: 14,
      fontWeight: 500,
    },
    subLinkItem: {
      marginLeft: !isHide ? 36 : 6,
      paddingLeft: isHide && !isMobile ? 6 : 16,
      paddingRight: isHide && !isMobile ? 6 : 16,
      width: isHide && !isMobile ? 34 : "auto",

      [theme.breakpoints.down(mobileSize)]: {
        marginLeft: 32,
      },
      opacity: 0.5,
      "&:hover": {
        opacity: 1,
      },
    },
    selectedSubLinkItem: {
      marginLeft: !isHide ? 36 : 5,
      opacity: 1,
      paddingLeft: isHide && !isMobile ? 6 : 16,
      paddingRight: isHide && !isMobile ? 6 : 16,
      width: isHide && !isMobile ? 34 : "auto",
      [theme.breakpoints.down(mobileSize)]: {
        marginLeft: 32,
      },

      "& ~ div .MuiChip-root": {
        backgroundColor: "rgba(130, 20, 220, 1)",
        color: "white",
      },
    },
    logOutIcon: {
      transform: "rotate(180deg)",
    },
    firstList: {
      display: "flex",
      flexDirection: "column",
      flex: "1",
      overflowX: "hidden",
      padding: "0 16px",
      [theme.breakpoints.down(mobileSize)]: {
        display: () => (menuIsOpen ? "block" : "none"),
        overflow: "scroll",
        padding: 0,
      },
    },
    systemList: {
      position: "relative",
      marginTop: isBrandRoute && isBrandUser ? 0 : 17,
      padding: "0 16px",

      "&::before": {
        content: '""',
        position: "absolute",
        left: 24,
        right: 24,
        top: -8,
        height: 1,
        background: "rgba(255, 255, 255, 0.2)",
        display: isBrandRoute && isBrandUser ? "none" : "block",
      },

      [theme.breakpoints.down(mobileSize)]: {
        display: () => (menuIsOpen ? "block" : "none"),
        marginBottom: 0,
        padding: 0,

        "&::before": {
          display: "none",
        },
      },
    },
    systemListWithoutLine: {
      position: "relative",
      marginTop: 0,
      padding: "0 16px",
    },
    "@keyframes attention": {
      "0%": {
        transform: "scale(0.85)",
      },
      "70%": {
        transform: "scale(0.9)",
      },
      "90%": {
        transform: "scale(1.1)",
      },
      "100%": {
        transform: "scale(1)",
      },
    },
    "@keyframes pulse-wave": {
      "0%": {
        boxShadow: "0px 0px 10px 2px rgba(255, 255, 255, 0.8)",
        borderRadius: "50%",
      },
      "70%": {
        boxShadow: "0px 0px 8px 1px rgba(255, 255, 255, 0.8)",
        borderRadius: "50%",
      },
      "90%": {
        boxShadow: "0px 0px 5px 1px rgba(255, 255, 255, 0.8)",
        borderRadius: "50%",
      },
      "100%": {
        boxShadow: "0 0 10px 0px rgba(255, 255, 255, 0)",
        borderRadius: "50%",
      },
    },
    listCounter: {
      position: "absolute",
      right: isHide ? 24 : "75%",
      top: isHide ? -2 : "5%",
      animation: "$attention 1s infinite, $pulse-wave 0.5s 1s infinite",
      // animation direction
      animationDirection: "alternate",
      // animation timing (ease-in-out)
      animationTimingFunction: "ease",

      "& .MuiChip-sizeSmall": {
        height: 22,
      },
      "& .MuiChip-labelSmall ": {
        paddingLeft: 7,
        paddingRight: 7,
      },
      [theme.breakpoints.down(mobileSize)]: {
        right: "unset",
        left: "8%",
        top: "12%",
      },
      pointerEvents: "none",
      cursor: "default",
      userSelect: "none",
    },
    listCounterOpened: {
      left: "unset",
      right: "8pt",
      top: "25%",
      [theme.breakpoints.down(mobileSize)]: {
        right: "20pt",
        // left: "50%",
        top: "26%",
      },
    },
    listCounterChip: {
      fontSize: 15,
      color: "white",
      backgroundColor: "#dc1414",
      boxShadow: "0 0 5px 2px rgba(130, 20, 220, 1)",
      fontFamily: fontFamilies[600],
      fontWeight: 600,

      padding: "3px",
      paddingBottom: "1px",
      borderRadius: 10,

      "&.MuiChip-sizeSmall": {
        height: "unset",
      },

      "& .MuiChip-labelSmall": {
        height: "unset",
        padding: 0,
        paddingLeft: 3,
        paddingRight: 3,
      },
    },
    menuHeader: {
      padding: "0 16px",
      [theme.breakpoints.down(mobileSize)]: {
        width: "100%",
        margin: "auto",
        marginTop: "11px",
        textAlign: "center",
        padding: 0,
      },
    },
    menuLogoWrapper: {
      position: "relative",
      marginBottom: 40,
      width: "100%",
      maxWidth: "100%",
      textAlign: "center",

      "&::after": {
        content: '""',
        position: "absolute",
        left: 8,
        right: 8,
        bottom: -20,
        height: 1,
        background: "rgba(255, 255, 255, 0.2)",
      },

      [theme.breakpoints.down(mobileSize)]: {
        marginBottom: 0,

        "&::after": {
          display: "none",
        },
      },
    },
    menuLogo: {
      width: "auto",
      maxWidth: "100%",
      maxHeight: 100,

      [theme.breakpoints.down(mobileSize)]: {
        width: 160,
        height: 42,
        textAlign: "center",
      },
    },
    menuOpenLogo: {
      display: "none",
      [theme.breakpoints.down(mobileSize)]: {
        display: "flex",
        alignItems: "center",
        height: 42,
        paddingLeft: 24,
      },
    },
    brandBackArrow: {
      textDecoration: "none",
      color: "white",
      padding: "16px",
      fontSize: 14,
      fontWeight: 300,
      [theme.breakpoints.down(mobileSize)]: {
        marginLeft: 8,
        marginTop: 8,
        padding: "16px 0",
      },
    },
    mainMenuWrapper: {
      height: "calc(100% - 80px)",
      overflowY: "auto",
      [theme.breakpoints.down(mobileSize)]: {
        height: !menuIsOpen ? 64 : "auto",
      },
      "&::-webkit-scrollbar": {
        display: "none",
      },
      "scrollbar-width": "none",
      "-ms-overflow-style": "none",
    },
    hideMenuItem: {
      color: "#999",
      height: 24,
      display: "flex",
      alignItems: "center",
      paddingLeft: isHide ? 12 : 16,
      fontSize: 14,
      padding: "16px 0",
      cursor: "pointer",
    },
    hideMenuIcon: {
      transition: ".25s linear",
    },
    version: {
      position: "absolute",
      color: "rgba(158, 162, 196, 0.56)",
      fontSize: 12,
      width: "calc(100% - 36px)",
      textAlign: "center",
      paddingTop: 6,
    },
    linkWithLine: {
      position: "relative",
      marginTop: 20,
      "&::before": {
        content: '""',
        position: "absolute",
        left: 8,
        right: 8,
        height: 1,
        top: -10,
        backgroundColor: "rgba(255, 255, 255, 0.2)",
      },
    },
    menuName: {
      display: "flex",
      alignItems: "center",
      height: 42,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%",
      overflow: "hidden",
    },
  }));

  const classes = useStyles();

  const checkHexIsLight = (rgb) => {
    const { r, g, b } = rgb;
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 64;
  };

  const getImagePrimaryColorFromUrl = async (url) => {
    const palette = await Vibrant.from(url)
      .quality(1)
      .clearFilters()
      .getPalette();

    const keys = Object.keys(palette);
    const mostPopulationKey = keys.reduce((acc, item) => {
      if (item === acc) return acc;
      if (palette[item].population > palette[acc].population) return item;
      return acc;
    }, keys[0]);

    return palette[mostPopulationKey].rgb;
  };

  const [isLightIcon, setIsLightIcon] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!brand || (isAnyAdmin && !isBrandRoute)) {
        return setIsLightIcon(true);
      }
      const [r, g, b] = await getImagePrimaryColorFromUrl(brand?.logo?.url);
      const rgb = { r, g, b };
      setIsLightIcon(checkHexIsLight(rgb));
    };

    fetchData();
  }, [isAnyAdmin, isBrandRoute, brand]);

  const [actionPopperOpen, setActionPopperOpen] = useState(false);

  const enableNavigationToTheRootView =
    isBrandRoute &&
    (userInfo.role.code === ROLES.admin ||
      userInfo.role.code === ROLES.superAdmin) &&
    ((isMobile && menuIsOpen) || !isMobile);
  return (
    <>
      <AlertDialog
        title="Are you sure you want to quit?"
        isOpen={isOpenLogout}
        handleNegativeActive={logOut}
        closeCrossHidden={true}
        handleClose={() => setIsOpenLogout(false)}
        buttonText="Quit"
      />
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor="left"
      >
        <div className={classes.mainMenuWrapper}>
          <Grid container className={classes.menuHeader} alignItems="center">
            <Grid item xs={2} className={classes.menuOpenLogo}>
              <MenuIcon
                style={{ color: "#808080" }}
                onClick={() => setMenuIsOpen(!menuIsOpen)}
              />
            </Grid>
            <Grid
              item
              xs={8}
              md={12}
              style={{
                display: "flex",
                justifyContent: "center",
                color: "white",
                gap: 8,
              }}
            >
              <MenuLogo
                isHide={isHide}
                isLightIcon={isLightIcon}
                isMobile={isMobile}
              />
            </Grid>
          </Grid>

          {isBrandRoute && brand && brandIsActivated === false && (
            <div style={{ margin: "0px 16px 12px" }}>
              <MenuBrandActivation
                activateDate={brand.activateDate}
                isHide={isHide}
              />
            </div>
          )}

          {enableNavigationToTheRootView && (
            <CustomLink
              style={{
                textDecoration: "none",
              }}
              to={BRANDS}
              content={
                <Grid
                  container
                  alignItems="center"
                  className={classes.brandBackArrow}
                  wrap="nowrap"
                >
                  {isHide ? (
                    <LightTooltip title="Exit" placement="right">
                      <ArrowBack style={{ marginRight: 16, marginLeft: 16 }} />
                    </LightTooltip>
                  ) : (
                    <ArrowBack style={{ marginRight: 16, marginLeft: 16 }} />
                  )}
                  {!isHide && !!brand && (
                    <div>{isMobile ? "Back" : brand.name}</div>
                  )}
                </Grid>
              }
            />
          )}

          {!isHide && isBrandUser && !!brand && brands.length > 1 && (
            <div style={{ padding: "0px 16px 12px" }}>
              <MenuBrandsPopper
                currentBrand={brand}
                brands={brands}
                actionPopperOpen={actionPopperOpen}
                setActionPopperOpen={setActionPopperOpen}
              />
            </div>
          )}

          {(!isMobile || (isMobile && menuIsOpen)) && (
            <>
              {!isHide &&
                isBrandUser &&
                !!brand &&
                brands.length === 1 &&
                enableNavigationToTheRootView && (
                  <div>
                    <Grid
                      container
                      alignItems="center"
                      className={classes.brandBackArrow}
                      wrap="nowrap"
                    >
                      <div style={{ marginLeft: 16 }}>{brand.name}</div>
                    </Grid>
                  </div>
                )}
              <List className={classes.firstList}>
                {routes.map(
                  (route) =>
                    route.access.length &&
                    route.access.includes(userInfo?.role?.code) &&
                    (!(route.name === "Couriers") || partner.isDelivery) && (
                      <div key={route.name}>
                        <CustomLink
                          to={route.path}
                          className={classes.link}
                          onClick={() =>
                            !route.children && setMenuIsOpen(false)
                          }
                          external={route.external}
                          content={
                            <ListItem
                              button
                              selected={
                                route.path === pathname ||
                                !!route.selected?.find((c) =>
                                  searchPathname(c, pathname)
                                )
                              }
                              className={clsx(classes.linkItem, {
                                [classes.linkItemActiveChild]:
                                  route.path !== pathname &&
                                  !!route.children?.find(
                                    (c) => c.path === pathname
                                  ),
                                [classes.linkWithLine]:
                                  route.name === "Settings",
                              })}
                              onClick={() =>
                                setOpenMenuItems((s) => ({
                                  ...s,
                                  [route.name]: !s[route.name],
                                }))
                              }
                            >
                              {isHide ? (
                                <LightTooltip
                                  title={route.name}
                                  placement="right"
                                >
                                  <ListItemIcon
                                    style={{
                                      minWidth: !isMobile && isHide ? 24 : 42,
                                    }}
                                  >
                                    {route.icon}
                                  </ListItemIcon>
                                </LightTooltip>
                              ) : (
                                <ListItemIcon
                                  style={{
                                    minWidth: !isMobile && isHide ? 24 : 42,
                                  }}
                                >
                                  {route.icon}
                                </ListItemIcon>
                              )}
                              {(isMobile || !isHide) && (
                                <ListItemText
                                  primary={
                                    <span className={classes.linkText}>
                                      {route.name}
                                    </span>
                                  }
                                />
                              )}

                              {!isHide &&
                                route.name === "Orders" &&
                                ordersCountMain > 0 &&
                                ((isBrandRoute && !openMenuItems[route.name]) ||
                                  !isBrandRoute) &&
                                !isBrandDriver && (
                                  <ListItemSecondaryAction
                                    className={classes.listCounter}
                                  >
                                    <Chip
                                      className={classes.listCounterChip}
                                      label={ordersCountMain}
                                      color="primary"
                                      size="small"
                                    />
                                  </ListItemSecondaryAction>
                                )}

                              {isHide &&
                                route.name === "Orders" &&
                                isBrandRoute &&
                                ordersCountMain > 0 &&
                                !isBrandDriver && (
                                  <div className={classes.listCounter}>
                                    <Chip
                                      className={classes.listCounterChip}
                                      label={ordersCountMain}
                                      color="primary"
                                      size="small"
                                    />
                                  </div>
                                )}

                              {!isHide &&
                                route.name === "Support" &&
                                supportCount > 0 && (
                                  <ListItemSecondaryAction
                                    className={classes.listCounter}
                                  >
                                    <Chip
                                      className={classes.listCounterChip}
                                      label={supportCount}
                                      color="primary"
                                      size="small"
                                    />
                                  </ListItemSecondaryAction>
                                )}

                              {isHide &&
                                route.name === "Support" &&
                                supportCount > 0 && (
                                  <div className={classes.listCounter}>
                                    <Chip
                                      className={classes.listCounterChip}
                                      label={supportCount}
                                      color="primary"
                                      size="small"
                                    />
                                  </div>
                                )}

                              {!isHide &&
                                route.name === "Messages" &&
                                messageCount > 0 && (
                                  <ListItemSecondaryAction
                                    className={classes.listCounter}
                                  >
                                    <Chip
                                      className={classes.listCounterChip}
                                      label={messageCount}
                                      color="primary"
                                      size="small"
                                    />
                                  </ListItemSecondaryAction>
                                )}

                              {isHide &&
                                route.name === "Messages" &&
                                messageCount > 0 && (
                                  <div className={classes.listCounter}>
                                    <Chip
                                      className={classes.listCounterChip}
                                      label={messageCount}
                                      color="primary"
                                      size="small"
                                    />
                                  </div>
                                )}
                            </ListItem>
                          }
                        />
                        {route.children && route.children.length && (
                          <Collapse
                            key={route.name + "_sublist"}
                            in={openMenuItems[route.name]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <List component="div" disablePadding>
                              {route.children.map((subRoute) => (
                                <CustomLink
                                  key={subRoute.name}
                                  to={subRoute.path}
                                  className={classes.link}
                                  external={subRoute.external}
                                  onClick={() => setMenuIsOpen(false)}
                                  style={
                                    isHide
                                      ? {
                                          position: "relative",
                                          left: 10,
                                          top: 0,
                                          zIndex: 10,
                                        }
                                      : {}
                                  }
                                  content={
                                    <ListItem
                                      button
                                      className={
                                        subRoute.path === pathname ||
                                        !!subRoute[
                                          isBrandDriver
                                            ? "selectedDriver"
                                            : "selected"
                                        ]?.find((c) => c === pathname)
                                          ? classes.selectedSubLinkItem
                                          : classes.subLinkItem
                                      }
                                    >
                                      {isHide ? (
                                        <LightTooltip
                                          title={subRoute.name}
                                          placement="right"
                                        >
                                          <ListItemIcon>
                                            {subRoute.icon}
                                          </ListItemIcon>
                                        </LightTooltip>
                                      ) : (
                                        <ListItemIcon style={{ minWidth: 36 }}>
                                          {subRoute.icon}
                                        </ListItemIcon>
                                      )}

                                      {(isMobile || !isHide) && (
                                        <ListItemText
                                          primary={
                                            <span className={classes.linkText}>
                                              {subRoute.name}
                                            </span>
                                          }
                                        />
                                      )}

                                      {!isHide &&
                                        subRoute.name === "New orders" &&
                                        ordersCountBrand > 0 &&
                                        openMenuItems[route.name] &&
                                        !isBrandDriver && (
                                          <ListItemSecondaryAction
                                            className={clsx(
                                              classes.listCounter,
                                              classes.listCounterOpened
                                            )}
                                          >
                                            <Chip
                                              className={
                                                classes.listCounterChip
                                              }
                                              label={ordersCountBrand}
                                              color="primary"
                                              size="small"
                                            />
                                          </ListItemSecondaryAction>
                                        )}
                                    </ListItem>
                                  }
                                />
                              ))}
                            </List>
                          </Collapse>
                        )}
                      </div>
                    )
                )}
              </List>

              {!isBrandManager &&
                !isBrandBudtender &&
                !isBrandDriver &&
                !isBrandRoute && (
                  <List className={classes.systemList}>
                    <Link
                      to={SETTINGS}
                      className={classes.link}
                      onClick={() => setMenuIsOpen(false)}
                    >
                      <ListItem
                        button
                        key="Settings"
                        className={classes.linkItem}
                        selected={SETTINGS === pathname}
                      >
                        {isHide ? (
                          <LightTooltip title="Settings" placement="right">
                            <ListItemIcon
                              style={{
                                minWidth: !isMobile && isHide ? 24 : 40,
                              }}
                            >
                              <SettingsIcon />
                            </ListItemIcon>
                          </LightTooltip>
                        ) : (
                          <ListItemIcon
                            style={{ minWidth: !isMobile && isHide ? 24 : 40 }}
                          >
                            <SettingsIcon />
                          </ListItemIcon>
                        )}
                        {(isMobile || !isHide) && (
                          <ListItemText
                            primary={
                              <span className={classes.linkText}>Settings</span>
                            }
                          />
                        )}
                      </ListItem>
                    </Link>
                    <ListItem
                      button
                      key="Logout"
                      className={classes.linkItem}
                      onClick={handleClickOpen}
                    >
                      {isHide ? (
                        <LightTooltip title="Logout" placement="right">
                          <ListItemIcon
                            style={{ minWidth: !isMobile && isHide ? 24 : 40 }}
                          >
                            <ExitToAppIcon className={classes.logOutIcon} />
                          </ListItemIcon>
                        </LightTooltip>
                      ) : (
                        <ListItemIcon
                          style={{ minWidth: !isMobile && isHide ? 24 : 40 }}
                        >
                          <ExitToAppIcon className={classes.logOutIcon} />
                        </ListItemIcon>
                      )}
                      {(isMobile || !isHide) && (
                        <ListItemText
                          primary={
                            <span className={classes.linkText}>Logout</span>
                          }
                        />
                      )}
                    </ListItem>
                  </List>
                )}

              {isBrandUser && (
                <List className={classes.systemListWithoutLine}>
                  <ListItem
                    button
                    key="Logout"
                    className={classes.linkItem}
                    onClick={handleClickOpen}
                  >
                    <ListItemIcon>
                      <ExitToAppIcon className={classes.logOutIcon} />
                    </ListItemIcon>
                    {(isMobile || !isHide) && (
                      <ListItemText
                        primary={
                          <span className={classes.linkText}>Logout</span>
                        }
                      />
                    )}
                  </ListItem>
                </List>
              )}
            </>
          )}
        </div>
        {!isMobile && (
          <div style={{ padding: "0 16px" }}>
            <div
              className={classes.hideMenuItem}
              onClick={() => setIsHide((prevState) => !prevState)}
            >
              <img
                src={chevronDownSvg}
                alt="arrow"
                className={classes.hideMenuIcon}
                style={{
                  transform: isHide ? "rotate(270deg)" : "rotate(90deg)",
                  marginRight: 16,
                }}
                width={24}
                height={24}
              />
              {!isHide && "Hide sidebar"}
            </div>
            <div className={classes.version}>v{APP_VERSION}</div>
          </div>
        )}
      </Drawer>
    </>
  );
};
